import React, { createContext, createRef, JSX, RefObject, useEffect, useImperativeHandle, useRef, useState } from "react";
import Field, { MultiField } from "./fields";
import * as yup from "yup";
import { useYupValidationResolver } from "./yupResolver";
import { FieldValues, FormState, RegisterOptions, useForm, UseFormRegister, UseFormRegisterReturn, UseFormSetValue, Control, UseFormWatch, DefaultValues, Path, UseFormReset, UseFormTrigger, UseFormClearErrors } from "react-hook-form";
import { FormHelperState, useFormHelperStore } from "./store";
import { flushSync } from "react-dom";
import LoadingModal from '@components/@general/loading-modal/loading-modal';
import {Portal} from "@mantine/core";
export const FORM_HELPER_CONSTS = {
  HORIZONTAL_FORM_CLASSES_WRAPPER: {
    label: "flex-grow m-auto pr-4 w-1/4",
    labelWrapper: "flex-grow",
    root: "flex",
  }
} as const;
type ConstantType = typeof FORM_HELPER_CONSTS;

export interface formHelperRef<T extends yup.AnyObject> {
  setCurrentValue: (value: { [s: string]: unknown; } | ArrayLike<unknown>) => void
  watch: UseFormWatch<T>
  setValue: UseFormSetValue<T>
  reset: UseFormReset<T>
  getData :any
}
interface RenderChildrenPropComponentProps<T extends yup.AnyObject> {
  Field: typeof Field
  toggleReadOnly: () => void
  watch: UseFormWatch<T>
  readOnly: boolean
  MultiField: typeof MultiField
  control: Control<T, any>
  register: UseFormRegister<T>

};
export type RenderPropComponentProps<T extends yup.AnyObject> = {
  children: (props: RenderChildrenPropComponentProps<T>) => React.ReactNode;
  validationSchema?: yup.ObjectSchema<T>;
  onSubmit?: (values: T) => void;
  errorHandler?: (errors: any) => void;
  readOnly?: boolean;
  horizontalForm?: boolean;
  debugMode?: boolean;
  defaultValues?: DefaultValues<T>;
  ref?: React.Ref<formHelperRef<T>>
  showLoadingUpload?:boolean
  methodBulkUpload?:boolean
  liveValidation?: boolean;                     
  validationMode?: 'onSubmit' | 'onBlur' | 'onChange' | 'all';  
  reValidateMode?: 'onChange' | 'onBlur';
};

interface FormHelperContextType<T extends FieldValues> {
  register: UseFormRegister<T>;
  formState: FormState<T>;
  formStore: FormHelperState;
  debugMode?: boolean;
  toggleReadOnly?: () => void;
  getValues: (name?: string) => any;
  setValue: UseFormSetValue<T>
  control: Control<T>;
  getSharedConsts: (field: keyof typeof FORM_HELPER_CONSTS) => ConstantType[keyof ConstantType];
  watch: UseFormWatch<T>;
  reset: () => void;
  defaultValues: DefaultValues<T>;
  inputRefs: RefObject<HTMLInputElement[] | null>
  Field: typeof Field
  PromisesRef: React.RefObject<Promises[] | null>
  trigger?: UseFormTrigger<T>;        
  clearErrors?: UseFormClearErrors<T>;   
}

export const FormHelperContext = createContext<FormHelperContextType<any>>({
  formState: {} as FormState<any>,
  getValues: () => { },
  setValue: () => { },
  formStore: {} as FormHelperState,
  register: function <TFieldName extends string = string>(name: TFieldName, options?: RegisterOptions<any, TFieldName> | undefined): UseFormRegisterReturn<TFieldName> {
    throw new Error("Function not implemented.");
  },
  control: {} as Control<any>,
  getSharedConsts: (field) => FORM_HELPER_CONSTS[field],
  watch: () => ({}) as any,
  reset: () => { },
  defaultValues: {},
  inputRefs: createRef(),
  Field: Field,
  PromisesRef: createRef(),
  trigger: undefined,            
  clearErrors: undefined, 
});

export type Promises = Promise<any>[] | (() => any)[] | null

export const useFormHelperContext = <T extends FieldValues>(): FormHelperContextType<T> => {
  return React.useContext(FormHelperContext) as FormHelperContextType<T>;
}

const FormHelper = <T extends yup.AnyObject>(props: RenderPropComponentProps<T>): JSX.Element => {

  const safeValidationSchema = (props.validationSchema ?? yup.object({} as T)) as yup.ObjectSchema<T>;
  const resolver = useYupValidationResolver(safeValidationSchema);

  const mode = props.validationMode ?? (props.liveValidation ? 'onChange' : 'onSubmit');   
  const reValidateMode = props.reValidateMode ?? (props.liveValidation ? 'onChange' : 'onChange'); 

  const { handleSubmit, register, formState, getValues, setValue, control, watch, reset, trigger, clearErrors} = useForm<T>({ resolver, shouldUnregister: false, defaultValues: props.defaultValues, mode, reValidateMode});

  // console.log("ini error state ", formState.errors)

  const formHelperState = useFormHelperStore(state => state);


  const toggleReadOnly = () => {
    formHelperState.setReadOnly(!formHelperState.readOnly)
  }
  const isReadOnly = formHelperState.readOnly;

  const getSharedConsts = <K extends keyof ConstantType>(field: K): ConstantType[K] => {
    return FORM_HELPER_CONSTS[field];
  };

  const passProps = { Field, toggleReadOnly, watch, getSharedConsts, readOnly: isReadOnly, MultiField, control, register };

  useEffect(() => {
    formHelperState.initialize(props)
  }, [props.horizontalForm, props.readOnly])

  const PromisesRef = useRef<Promises[]>([])
  const setDefaultValue = (value: { [s: string]: unknown; } | ArrayLike<unknown>) => {
    Object.entries(value).forEach(([key, value]) => {
      setValue(key as Path<T>, value as T[keyof T], { shouldDirty: false });
    });
  }

  const inputRefs = useRef<HTMLInputElement[]>([])

  const HandleReset = () => {
    for (const ref of inputRefs.current) {
      if (ref) {
        if (ref.value) {
          ref.value = ""
        }
      }
    }
    reset()
  }
  useImperativeHandle(props.ref, () => ({
    reset: HandleReset,
    setValue,
    watch,
    setCurrentValue: (value: { [s: string]: unknown; } | ArrayLike<unknown>) => {
      setDefaultValue(value)
    },
    getData:getValues(),
    trigger,
    clearErrors
  }))


  const defaultValue = useRef(JSON.stringify(props.defaultValues))

  useEffect(() => {
    const stringifyValue = JSON.stringify(props.defaultValues)
    if (stringifyValue !== defaultValue.current) {
      if (props.defaultValues) {
        setDefaultValue(props.defaultValues);
      }
    }
  }, [props.defaultValues])


  const [debugModeValue, setDebugModeValue] = useState<T | undefined>(undefined);

  const errHandler = (errors: any) => {
    console.log(errors);
  }
  const [loading, setLoading] = useState(false);
  const handleFormSubmit = handleSubmit(async (data: T) => {
    // await props.onLoadingChange?.(true);
    if (props.showLoadingUpload) {
      await setLoading?.(true);
    }
    try {
      // debugger
      // send parallel upload file
      if (props.methodBulkUpload) {
        await Promise.all(
        (PromisesRef.current || [])
        .filter((fn) => typeof fn === "function")
        .map((fn: Function) => fn())
        );
        await setLoading?.(false);
        if (props.onSubmit) {
          const freshFormData = getValues();
          await props.onSubmit(freshFormData as T);
        }
      }else{
        // send sequence upload file
        await (PromisesRef.current || [])
        .filter((fn) => typeof fn === "function")
        .reduce(
          (prev, fn : Function) => prev.then(() => fn()),
          Promise.resolve()
        ).then(async()=>{
          await setLoading(false);
          if (props.onSubmit) {
             const freshFormData = getValues();
             await props.onSubmit(freshFormData as T);
          }
        })
      }
        
        // await new Promise((resolve) => setTimeout(resolve, 1000));
        // await Promise.all(((PromisesRef.current)?.map((fn: any) => (fn as any)()) || []));
      // Call the onSubmit function provided via props
    } catch (error) {
      // Handle errors using the errorHandler provided via props
      if (props.errorHandler) {
        props.errorHandler(error);
      } else {
        console.error('Form submission error:', error);
      }
      await setLoading(false);
    }
  })

  return (<>
        <Portal>
        <LoadingModal opened={loading} message="Please wait, this could take a moment." />
        </Portal>
    <form onSubmit={handleFormSubmit} className="w-full">
      <FormHelperContext.Provider value={{ register, formState, formStore: formHelperState, toggleReadOnly, getValues, setValue, control, getSharedConsts, watch, reset, defaultValues: props.defaultValues, Field, PromisesRef: PromisesRef, inputRefs, trigger, clearErrors}}>
        {props.children(passProps)}
      </FormHelperContext.Provider>

      {props.debugMode && (
        <div className="mt-10">
          <hr />
          <h3>Debug Mode</h3>
          <pre>
            {JSON.stringify(debugModeValue, null, 2)}
          </pre>
          <h3>Debug Validation</h3>
          <pre>
            {JSON.stringify(formState.errors, null, 2)}
          </pre>
          <button onClick={() => { setDebugModeValue(getValues()) }}>Refresh Value</button>
        </div>
      )}

    </form >
      </>
  );
};

export default FormHelper;
